'use client';

import { memo } from 'react';
import { Box, HStack, token } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { formatDuration } from '@terra/ui-formatters';
import { useTranslations } from 'next-intl';
import type { AttackPathEventNodeData } from './attack-path-event-node.types';
import { useAttackPathCampaignHover } from './attack-path-campaign-hover-context';
import {
  type AttackPathCampaignOutcome,
  resolveAttackPathCampaignOutcome,
  translucentColor,
} from './attack-path-event-visuals';

const OUTCOME_COLOR: Record<AttackPathCampaignOutcome, string> = {
  failed: token('colors.fg.error'),
  succeeded: token('colors.fg.success'),
  pending: token('colors.border.accent.default'),
};

type InternalNetworkAttackPathCampaignFrameNodeProps = {
  data: AttackPathEventNodeData;
  selected?: boolean;
};

type CampaignFrameLabelProps = {
  title: string;
  color: string;
  durationLabel: string | null;
  statusLabel: string | null;
  isRunning: boolean;
};

const CampaignFrameLabel = ({
  title,
  color,
  durationLabel,
  statusLabel,
  isRunning,
}: CampaignFrameLabelProps) => (
  <HStack
    position="absolute"
    top="0"
    left="3"
    maxW="calc(100% - 24px)"
    gap="1.5"
    px="2"
    py="0.5"
    bg="bg.default"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="border.subtle"
    borderRadius="full"
    flexWrap="nowrap"
    style={{ transform: 'translateY(-50%)' }}
  >
    <Box
      flexShrink={0}
      w="6px"
      h="6px"
      borderRadius="full"
      style={{
        backgroundColor: color,
        animation: isRunning
          ? 'attack-path-status-dot-pulse 1.4s ease-in-out infinite'
          : undefined,
      }}
    />
    <MetaDefault
      title={title}
      color="fg.default"
      fontWeight="medium"
      truncate
      minW={0}
    >
      {title}
    </MetaDefault>
    {durationLabel ? (
      <MetaDefault color="fg.muted" flexShrink={0}>
        · {durationLabel}
      </MetaDefault>
    ) : null}
    {statusLabel ? (
      <MetaDefault flexShrink={0} style={{ color }}>
        · {statusLabel}
      </MetaDefault>
    ) : null}
  </HStack>
);

export const AttackPathCampaignFrameNode = memo(
  ({ data, selected }: InternalNetworkAttackPathCampaignFrameNodeProps) => {
    const tExecutions = useTranslations('internalNetwork.executions');
    const campaignHover = useAttackPathCampaignHover();
    const isHoverLit =
      data.campaignId != null && data.campaignId === campaignHover.campaignId;
    const outcome = resolveAttackPathCampaignOutcome(data.campaignStatus);
    const isPinned = Boolean(data.highlighted || selected);
    const isSignalExecution = data.role === 'SIGNAL_EXECUTION';
    const isLit =
      isPinned || data.bootActive || isHoverLit || data.isCampaignRunning;
    const color = OUTCOME_COLOR[outcome];
    const visibleOpacity = data.hidden || !isLit ? 0 : 1;
    const statusLabel = data.isCampaignRunning
      ? tExecutions('status.RUNNING')
      : null;

    return (
      <Box
        position="relative"
        w="full"
        h="full"
        borderWidth="1px"
        borderStyle={isPinned ? 'solid' : 'dashed'}
        borderRadius="xl"
        opacity={visibleOpacity}
        pointerEvents={isSignalExecution ? 'auto' : 'none'}
        cursor={isSignalExecution ? 'pointer' : undefined}
        style={{
          borderColor: translucentColor({
            color,
            percent: isPinned ? 70 : 45,
          }),
          backgroundColor: translucentColor({
            color,
            percent: isPinned ? 6 : 3,
          }),
          transform: data.hidden ? 'scale(0.98)' : 'scale(1)',
          animation:
            data.bootActive && !data.hidden
              ? 'attack-path-frame-boot 0.85s ease-out'
              : undefined,
          transition:
            'opacity 0.2s ease, border-color 0.15s ease, background-color 0.15s ease',
        }}
      >
        <CampaignFrameLabel
          title={data.objective ?? data.title}
          color={color}
          durationLabel={
            data.durationSeconds !== null
              ? formatDuration(data.durationSeconds * 1000)
              : null
          }
          statusLabel={statusLabel}
          isRunning={Boolean(data.isCampaignRunning)}
        />
      </Box>
    );
  },
);
AttackPathCampaignFrameNode.displayName = 'AttackPathCampaignFrameNode';
